import { FC, useCallback, useRef, useState } from 'react';
import AvatarEditor from 'react-avatar-editor';
import DragAndDrop from '@components/drag_and_drop';
import styles from './styles.module.scss';
import Typography from '@components/common/typography';
import { IModalComponentProps } from '@context/modal_context';
import Button from '@components/common/button';
import { useLocales } from '@hooks/helpers/useLocales';
import BarPicker from '@components/bar_picker';
import { useTranslation } from 'react-i18next';
import { useUserContext } from '@hooks/context/useUserContext';
import Webcam from 'react-webcam';
import { base64ToFile } from '@utils/index';
import { useApiMutation } from '@hooks/api/useApiMutation';
import { apiUserService } from '@api';

const UploadAvatarModal: FC<
    IModalComponentProps<{ isBusiness: boolean; customTitle: boolean; avatar?: string }>
> = ({ onSubmit, changeModalData, modalData, onClose }) => {
    const { t } = useTranslation('modals', { keyPrefix: 'upload_avatar' });
    const { submitLocale, requestSuccessLocale, requestErrorLocale } = useLocales();
    const [image, setImage] = useState<File>();
    const [scale, setScale] = useState(0);
    const editorRef = useRef<AvatarEditor | null>(null);
    const webcamRef = useRef<any>(null);
    const [isWebcam, setIsWebcam] = useState(false);
    const { user, refetchUser } = useUserContext();

    const handleSave = () => {
        if (editorRef.current) {
            const canvas = editorRef.current.getImageScaledToCanvas().toDataURL();
            changeModalData({ ...modalData, avatar: canvas });
            onSubmit && onSubmit({ ...modalData, avatar: canvas });
        }
    };

    const handleMakePhoto = useCallback(() => {
        const imageSrc = webcamRef.current.getScreenshot();
        const mimetype = webcamRef.current.getScreenshot().split(';')?.[0].split('/')?.[1];
        setImage(base64ToFile(imageSrc, 'image', mimetype));
        setIsWebcam(false);
    }, [webcamRef]);

    const { handleRequest: handleDeleteAvatar, isLoading: isDeleteAvatarLoading } = useApiMutation({
        method: () => apiUserService.deleteAvatar(),
        onSuccess: () => {
            changeModalData({ ...modalData, avatar: undefined });
            onClose();
            requestSuccessLocale('delete_avatar');
            refetchUser();
        },
        onError: requestErrorLocale
    });

    return (
        <div className={styles.wrapper}>
            <Typography className={styles.title} variant="h4" alignment="center">
                {modalData?.isBusiness
                    ? t(modalData?.avatar ? 'update_logo' : 'add_logo')
                    : modalData?.customTitle
                      ? modalData?.customTitle
                      : t(user?.profile?.avatar ? 'update_avatar' : 'add_avatar')}
            </Typography>
            {image ? (
                <>
                    <AvatarEditor
                        ref={editorRef}
                        image={image && URL.createObjectURL(image)}
                        width={250}
                        height={250}
                        border={50}
                        borderRadius={125}
                        scale={1 + scale / 100}
                        className={styles.editor}
                    />
                    <div className={styles.bar}>
                        <BarPicker
                            step={1}
                            value={scale}
                            onChange={(val) => setScale(val)}
                            min={0}
                            max={100}
                        />
                    </div>
                    <Typography variant="body4">
                        Make sure your photo clearly shows your face
                    </Typography>
                    <div className={styles.actions}>
                        <Button size="medium" variant="primary" onClick={handleSave}>
                            {submitLocale('save')}
                        </Button>
                        <Button
                            size="medium"
                            variant="text-dark"
                            onClick={() => setImage(undefined)}>
                            {submitLocale('choose_another_photo')}
                        </Button>
                    </div>
                </>
            ) : isWebcam ? (
                <>
                    <Webcam
                        screenshotFormat="image/png"
                        style={{ borderRadius: 8 }}
                        width="100%"
                        ref={webcamRef}
                    />
                    <div className={styles.actions}>
                        <Button size="medium" variant="primary" onClick={() => setIsWebcam(false)}>
                            {submitLocale('cancel')}
                        </Button>
                        <Button size="medium" variant="primary" onClick={handleMakePhoto}>
                            {submitLocale('make_photo')}
                        </Button>
                    </div>
                </>
            ) : (
                <>
                    <DragAndDrop
                        onChange={(val) => setImage(val)}
                        onTakePhoto={() => setIsWebcam(true)}
                    />
                    <Button variant="outlined-primary" size="medium" onClick={onClose}>
                        {submitLocale('cancel')}
                    </Button>
                    {modalData?.avatar && !modalData?.isBusiness && (
                        <Button
                            variant="text-dark"
                            onClick={handleDeleteAvatar}
                            isLoading={isDeleteAvatarLoading}>
                            {submitLocale('delete_current_photo')}
                        </Button>
                    )}
                </>
            )}
        </div>
    );
};

export default UploadAvatarModal;
